import { useEffect, useRef, useState } from 'react';
import * as albumsApi from '../api/albums.api';
import type { Album } from '../api/albums.api';

// 모듈 스코프 in-flight 맵 — 동일 id 동시 요청 dedupe
const inflight = new Map<string, Promise<Album>>();

const CACHE_TTL = 3_600_000; // 1시간

interface AlbumCacheEntry {
  data: Album;
  at: number;
}

function readAlbumCache(id: string): AlbumCacheEntry | null {
  try {
    const raw = localStorage.getItem(`album-cache-${id}`);
    if (!raw) return null;
    const entry = JSON.parse(raw) as AlbumCacheEntry;
    if (Date.now() - entry.at >= CACHE_TTL) return null;
    return entry;
  } catch {
    return null;
  }
}

// 가장 오래된 album-cache-* 키 찾아 evict (LRU 보호).
function findOldestAlbumCacheKey(excludeId: string): string | null {
  let oldestKey: string | null = null;
  let oldestAt = Infinity;
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith('album-cache-') || k === `album-cache-${excludeId}`) continue;
    try {
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const entry = JSON.parse(raw);
      const at = typeof entry?.at === 'number' ? entry.at : 0;
      if (at < oldestAt) {
        oldestAt = at;
        oldestKey = k;
      }
    } catch {
      // 손상된 entry 는 즉시 evict 후보
      oldestKey = k;
      oldestAt = -Infinity;
    }
  }
  return oldestKey;
}

// 캐시용으로 base64 (data:) image 필드를 떼낸 lightweight 사본 생성.
// 폼 필드 8개 × ~220KB → ~100KB 미만으로 축소되어 quota 부담 자체를 제거.
// 에러 시 fallback 에서는 이미지가 안 보일 수 있지만 메타데이터는 보존되어
// "앨범을 불러올 수 없습니다" 다이얼로그 회귀를 막는 trade-off.
function stripDataUrls(input: unknown): unknown {
  if (typeof input === 'string') {
    return input.startsWith('data:') && input.length > 1024 ? '' : input;
  }
  if (!input || typeof input !== 'object') return input;
  if (Array.isArray(input)) return input.map(stripDataUrls);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
    out[k] = stripDataUrls(v);
  }
  return out;
}

// 부팅 시 1회 호출 — 기존 album-cache-* 항목들에서 base64 이미지를 떼어내
// localStorage 사이즈를 즉시 회복한다. SWEEP_SENTINEL 로 중복 실행 방지.
const SWEEP_SENTINEL = 'album-cache-sweep-v1';
export function sweepAlbumCacheBase64Once(): void {
  try {
    if (localStorage.getItem(SWEEP_SENTINEL)) return;
    let recovered = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith('album-cache-') || k === SWEEP_SENTINEL) continue;
      try {
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        const before = raw.length;
        const parsed = JSON.parse(raw);
        const stripped = stripDataUrls(parsed);
        const after = JSON.stringify(stripped);
        if (after.length < before) {
          localStorage.setItem(k, after);
          recovered += before - after.length;
        }
      } catch {
        // 손상된 entry 는 즉시 제거
        localStorage.removeItem(k);
      }
    }
    localStorage.setItem(SWEEP_SENTINEL, String(Date.now()));
    if (recovered > 0) {
      console.info(`[album-cache] swept ${Math.round(recovered / 1024)}KB of base64 images`);
    }
  } catch {
    // localStorage 자체가 막혔으면 sweep 포기
  }
}

function writeAlbumCache(id: string, data: Album): void {
  const lightweight = stripDataUrls(data) as Album;
  const entry: AlbumCacheEntry = { data: lightweight, at: Date.now() };
  const value = JSON.stringify(entry);
  // QuotaExceededError 발생 시 LRU evict 후 재시도. 5회까지 비워보고 그래도
  // 실패하면 캐시 쓰기 포기 (런타임 동작은 영향 없음).
  for (let i = 0; i < 5; i++) {
    try {
      localStorage.setItem(`album-cache-${id}`, value);
      return;
    } catch (err: any) {
      if (err?.name !== 'QuotaExceededError') return;
      const evict = findOldestAlbumCacheKey(id);
      if (!evict) return;
      localStorage.removeItem(evict);
    }
  }
}

export interface UseAlbumResult {
  album: Album | null;
  loading: boolean;
  error: Error | null;
  fromCache: boolean;
  cachedAt: Date | null;
  reload: () => void;
}

export function useAlbum(id: string | undefined | null): UseAlbumResult {
  const [album, setAlbum] = useState<Album | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [error, setError] = useState<Error | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [cachedAt, setCachedAt] = useState<Date | null>(null);
  const mountedRef = useRef(true);
  const loadedIdRef = useRef<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!id) {
      setAlbum(null);
      setLoading(false);
      return;
    }
    if (loadedIdRef.current === id && reloadTick === 0) return;

    setLoading(true);
    setError(null);

    let promise = inflight.get(id);
    if (!promise) {
      promise = albumsApi.getAlbum(id);
      inflight.set(id, promise);
      promise.finally(() => {
        inflight.delete(id);
      });
    }

    promise
      .then((data) => {
        if (!mountedRef.current) return;
        setAlbum(data);
        setFromCache(false);
        setCachedAt(null);
        loadedIdRef.current = id;
        writeAlbumCache(id, data);
      })
      .catch((e: unknown) => {
        if (!mountedRef.current) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        // 에러 시 localStorage 캐시에서 복원 (TTL 1시간)
        const cached = readAlbumCache(id);
        if (cached) {
          setAlbum(cached.data);
          setFromCache(true);
          setCachedAt(new Date(cached.at));
        }
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, [id, reloadTick]);

  return { album, loading, error, fromCache, cachedAt, reload: () => setReloadTick((t) => t + 1) };
}
